import React, { useState, useEffect, useMemo, useCallback } from 'react';

const PRIMARY = '#0052CC';

export default function AuthPresetsPage() {
  const [presets, setPresets] = useState([]);
  const [zones, setZones] = useState([]);
  const [onuTypes, setOnuTypes] = useState([]);
  const [speedProfiles, setSpeedProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editPreset, setEditPreset] = useState(null);
  const [form, setForm] = useState({ name: '', onu_type_id: '', line_profile: '', service_profile: '', vlan_id: '', zone_id: '', speed_profile_id: '', onu_mode: 'routing', gpon_channel: 'gpon', is_default: false });
  const [olts, setOlts] = useState([]);
  const [selectedOltId, setSelectedOltId] = useState('');
  const [oltProfiles, setOltProfiles] = useState({ line_profiles: [], service_profiles: [] });
  const [approvedServices, setApprovedServices] = useState([]);
  const [updatingServiceId, setUpdatingServiceId] = useState(null);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const api = useMemo(() => {
    const token = localStorage.getItem('session_token');
    const headers = { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
    return {
      get: (url) => fetch(url, { headers }).then(r => r.json()),
      post: (url, body) => fetch(url, { method: 'POST', headers, body: JSON.stringify(body) }).then(r => r.json()),
      put: (url, body) => fetch(url, { method: 'PUT', headers, body: JSON.stringify(body) }).then(r => r.json()),
      del: (url) => fetch(url, { method: 'DELETE', headers }).then(r => r.json()),
    };
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, z, t, s, o, approved] = await Promise.all([
        api.get('/api/noc/auth-presets'),
        api.get('/api/noc/zones'),
        api.get('/api/noc/onu-types'),
        api.get('/api/noc/speed-profiles'),
        api.get('/api/noc/olts'),
        api.get('/api/noc/approved-dma-services'),
      ]);
      setPresets(p.items || []);
      setZones(z.items || []);
      setOnuTypes(t.items || []);
      setSpeedProfiles(s.items || []);
      const oltList = o.items || o || [];
      setOlts(oltList);
      setApprovedServices(approved.items || []);
      if (oltList.length > 0 && !selectedOltId) setSelectedOltId(String(oltList[0].id));
    } catch (e) { setError('Failed to load data'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadAll(); }, [loadAll]);
  const loadOltProfiles = useCallback(async (oltId) => {
    if (!oltId) return;
    setLoadingProfiles(true);
    try {
      const r = await api.get(`/api/noc/olts/${oltId}/olt-profiles-cli`);
      setOltProfiles({ line_profiles: r.line_profiles || [], service_profiles: r.service_profiles || [] });
    } catch (e) { setOltProfiles({ line_profiles: [], service_profiles: [] }); }
    finally { setLoadingProfiles(false); }
  }, [api]);
  useEffect(() => { if (selectedOltId) loadOltProfiles(selectedOltId); }, [selectedOltId, loadOltProfiles]);

  const openAdd = () => {
    setEditPreset(null);
    setForm({ name: '', onu_type_id: '', line_profile: '', service_profile: '', vlan_id: '', zone_id: '', speed_profile_id: '', onu_mode: 'routing', gpon_channel: 'gpon', is_default: false });
    setShowForm(true);
  };
  const openEdit = (p) => {
    setEditPreset(p);
    setForm({ name: p.name, onu_type_id: p.onu_type_id || '', line_profile: p.line_profile || '', service_profile: p.service_profile || '', vlan_id: p.vlan_id || '', zone_id: p.zone_id || '', speed_profile_id: p.speed_profile_id || '', onu_mode: p.onu_mode || 'routing', gpon_channel: p.gpon_channel || 'gpon', is_default: !!p.is_default });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Preset name is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, onu_type_id: form.onu_type_id ? parseInt(form.onu_type_id) : null, zone_id: form.zone_id ? parseInt(form.zone_id) : null, speed_profile_id: form.speed_profile_id ? parseInt(form.speed_profile_id) : null, vlan_id: form.vlan_id ? parseInt(form.vlan_id) : null };
      if (editPreset) { await api.put(`/api/noc/auth-presets/${editPreset.id}`, payload); }
      else { await api.post('/api/noc/auth-presets', payload); }
      setShowForm(false); loadAll();
    } catch (e) { setError('Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this preset?')) return;
    try { await api.del(`/api/noc/auth-presets/${id}`); loadAll(); }
    catch (e) { setError('Delete failed'); }
  };

  const handleSetDefault = async (id) => {
    try { await api.post(`/api/noc/auth-presets/${id}/set-default`, {}); loadAll(); }
    catch (e) { setError('Failed to set default'); }
  };

  const handleToggleApprovedService = async (service) => {
    const nextActive = !Number(service.is_active);
    setUpdatingServiceId(service.service_id);
    setError('');
    try {
      const result = await api.put(`/api/noc/approved-dma-services/${service.service_id}`, { is_active: nextActive });
      if (!result.success) throw new Error(result.error || 'Update failed');
      setApprovedServices(items => items.map(item => item.service_id === service.service_id ? { ...item, is_active: nextActive ? 1 : 0 } : item));
    } catch (e) {
      setError(e.message || 'Failed to update approved service plan');
    } finally {
      setUpdatingServiceId(null);
    }
  };

  return (
    <div style={{ padding: '24px', background: '#f5f7fa', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#172B4D' }}>Authorization Presets</h2>
          <p style={{ margin: '4px 0 0', color: '#6B778C', fontSize: 13 }}>Pre-configured ONU authorization templates for quick deployment</p>
        </div>
        <button onClick={openAdd} style={{ background: PRIMARY, color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
          + Add Preset
        </button>
      </div>

      {error && <div style={{ background: '#FFEBE6', border: '1px solid #FF5630', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#BF2600', fontSize: 13 }}>{error}</div>}

      <section style={{ background: '#fff', border: '1px solid #DFE1E6', borderRadius: 10, marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #DFE1E6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, color: '#172B4D', fontSize: 16, fontWeight: 700 }}>Approved Internet Plans</h3>
            <p style={{ margin: '4px 0 0', color: '#6B778C', fontSize: 12 }}>Only enabled plans appear in ONU Authorization. Updating this catalog never changes existing customer, Radius, or ONU assignments.</p>
          </div>
          <div style={{ background: '#E3FCEF', color: '#006644', padding: '5px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {approvedServices.filter(s => Number(s.is_active)).length} active / {approvedServices.length}
          </div>
        </div>
        {approvedServices.length === 0 ? (
          <div style={{ padding: 18, color: '#6B778C', fontSize: 13 }}>No approved plans are configured yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#F4F5F7', color: '#5E6C84', textAlign: 'left' }}>
                  <th style={{ padding: '10px 14px', fontWeight: 700 }}>Service</th>
                  <th style={{ padding: '10px 14px', fontWeight: 700 }}>Speed</th>
                  <th style={{ padding: '10px 14px', fontWeight: 700 }}>Status</th>
                  <th style={{ padding: '10px 14px', fontWeight: 700, textAlign: 'right' }}>Authorization</th>
                </tr>
              </thead>
              <tbody>
                {approvedServices.map(service => {
                  const isActive = Number(service.is_active) === 1;
                  const down = Number(service.downrate || 0) / 1048576;
                  const up = Number(service.uprate || 0) / 1048576;
                  return (
                    <tr key={service.service_id} style={{ borderTop: '1px solid #F4F5F7', opacity: isActive ? 1 : 0.6 }}>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ color: '#172B4D', fontWeight: 700 }}>{service.srvname || `Service #${service.service_id}`}</div>
                        <div style={{ color: '#6B778C', fontSize: 11 }}>ID {service.service_id}</div>
                      </td>
                      <td style={{ padding: '10px 14px', color: '#344563' }}>{down > 0 ? `${down} / ${up || down} Mbps` : '—'}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ background: isActive ? '#E3FCEF' : '#F4F5F7', color: isActive ? '#006644' : '#6B778C', padding: '3px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                          {isActive ? 'APPROVED' : 'HIDDEN'}
                        </span>
                      </td>
                      <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                        <button onClick={() => handleToggleApprovedService(service)} disabled={updatingServiceId === service.service_id}
                          style={{ background: isActive ? '#FFEBE6' : '#E3FCEF', color: isActive ? '#BF2600' : '#006644', border: 'none', borderRadius: 4, padding: '5px 10px', cursor: updatingServiceId === service.service_id ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600 }}>
                          {updatingServiceId === service.service_id ? 'Saving…' : isActive ? 'Hide from Authorization' : 'Enable for Authorization'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6B778C' }}>Loading...</div>
      ) : presets.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #DFE1E6', padding: 40, textAlign: 'center', color: '#6B778C' }}>
          No authorization presets found. Click "+ Add Preset" to create one.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {presets.map(p => (
            <div key={p.id} style={{ background: '#fff', borderRadius: 10, border: `2px solid ${p.is_default ? PRIMARY : '#DFE1E6'}`, padding: 20, position: 'relative', boxShadow: p.is_default ? '0 2px 12px rgba(0,82,204,0.15)' : 'none' }}>
              {p.is_default && (
                <div style={{ position: 'absolute', top: 12, right: 12, background: PRIMARY, color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>DEFAULT</div>
              )}
              <h4 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: '#172B4D', paddingRight: p.is_default ? 70 : 0 }}>{p.name}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', fontSize: 13 }}>
                {[
                  ['ONU Type', p.onu_type_name || '—'],
                  ['Zone', p.zone_name || '—'],
                  ['Speed Profile', p.speed_profile_name || '—'],
                  ['VLAN', p.vlan_id || '—'],
                  ['Line Profile', p.line_profile || '—'],
                  ['Service Profile', p.service_profile || '—'],
                  ['ONU Mode', p.onu_mode || '—'],
                  ['GPON Channel', p.gpon_channel || '—'],
                ].map(([label, val]) => (
                  <div key={label}>
                    <span style={{ color: '#6B778C', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
                    <div style={{ color: '#172B4D', fontWeight: 500, marginTop: 1 }}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 16, borderTop: '1px solid #F4F5F7', paddingTop: 12 }}>
                {!p.is_default && (
                  <button onClick={() => handleSetDefault(p.id)} style={{ background: '#DEEBFF', color: PRIMARY, border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Set Default</button>
                )}
                <button onClick={() => openEdit(p)} style={{ background: '#E3FCEF', color: '#006644', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>Edit</button>
                <button onClick={() => handleDelete(p.id)} style={{ background: '#FFEBE6', color: '#BF2600', border: 'none', borderRadius: 4, padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 580, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#172B4D' }}>{editPreset ? 'Edit Preset' : 'Add Preset'}</h3>
            {error && <div style={{ background: '#FFEBE6', border: '1px solid #FF5630', borderRadius: 5, padding: '8px 12px', marginBottom: 14, color: '#BF2600', fontSize: 13 }}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Preset Name *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Standard Home 100M"
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>ONU Type</label>
                <select value={form.onu_type_id} onChange={e => setForm(p => ({ ...p, onu_type_id: e.target.value }))}
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none' }}>
                  <option value="">Select ONU Type</option>
                  {onuTypes.map(t => <option key={t.id} value={t.id}>{t.model_name} ({t.manufacturer})</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Zone</label>
                <select value={form.zone_id} onChange={e => setForm(p => ({ ...p, zone_id: e.target.value }))}
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none' }}>
                  <option value="">Select Zone</option>
                  {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Speed Profile</label>
                <select value={form.speed_profile_id} onChange={e => setForm(p => ({ ...p, speed_profile_id: e.target.value }))}
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none' }}>
                  <option value="">Select Speed Profile</option>
                  {speedProfiles.map(s => <option key={s.id} value={s.id}>{s.name} ({s.download_speed}/{s.upload_speed})</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Load Profiles from OLT</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <select value={selectedOltId} onChange={e => setSelectedOltId(e.target.value)}
                    style={{ flex: 1, padding: '8px 10px', border: '1px solid #DFE1E6', borderRadius: 4, fontSize: 13 }}>
                    <option value="">-- Select OLT --</option>
                    {olts.map(o => <option key={o.id} value={String(o.id)}>{o.name || o.ip_address}</option>)}
                  </select>
                  <button type="button" onClick={() => loadOltProfiles(selectedOltId)} disabled={!selectedOltId || loadingProfiles}
                    style={{ padding: '8px 14px', background: '#0052CC', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {loadingProfiles ? '⏳ Loading...' : '🔄 Load Profiles'}
                  </button>
                </div>
                {oltProfiles.line_profiles.length > 0 && <div style={{ fontSize: 12, color: '#36B37E', marginTop: 4 }}>✅ {oltProfiles.line_profiles.length} line profiles, {oltProfiles.service_profiles.length} service profiles loaded</div>}
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Line Profile</label>
                       <select value={form.line_profile} onChange={e => setForm(p => ({ ...p, line_profile: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #DFE1E6', borderRadius: 4, fontSize: 13 }}>
                  <option value="">-- Select Line Profile --</option>
                  {loadingProfiles ? <option disabled>Loading from OLT...</option> : null}
                  {oltProfiles.line_profiles.map(lp => (
                    <option key={lp.id} value={String(lp.id)}>{lp.id} — {lp.name}</option>
                  ))}
                  {oltProfiles.line_profiles.length === 0 && !loadingProfiles ? (
                    <option disabled>No profiles loaded (select OLT above)</option>
                  ) : null}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Service Profile</label>
                       <select value={form.service_profile} onChange={e => setForm(p => ({ ...p, service_profile: e.target.value }))}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #DFE1E6', borderRadius: 4, fontSize: 13 }}>
                  <option value="">-- Select Service Profile --</option>
                  {loadingProfiles ? <option disabled>Loading from OLT...</option> : null}
                  {oltProfiles.service_profiles.map(sp => (
                    <option key={sp.id} value={String(sp.id)}>{sp.id} — {sp.name}</option>
                  ))}
                  {oltProfiles.service_profiles.length === 0 && !loadingProfiles ? (
                    <option disabled>No profiles loaded (select OLT above)</option>
                  ) : null}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>VLAN ID</label>
                <input type="number" value={form.vlan_id} onChange={e => setForm(p => ({ ...p, vlan_id: e.target.value }))} placeholder="e.g. 100"
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>ONU Mode</label>
                <select value={form.onu_mode} onChange={e => setForm(p => ({ ...p, onu_mode: e.target.value }))}
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none' }}>
                  <option value="routing">Routing</option>
                  <option value="bridging">Bridging</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>GPON Channel</label>
                <select value={form.gpon_channel} onChange={e => setForm(p => ({ ...p, gpon_channel: e.target.value }))}
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none' }}>
                  <option value="gpon">GPON</option>
                  <option value="xgpon">XG-PON</option>
                  <option value="xgspon">XGS-PON</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" id="is_default" checked={form.is_default} onChange={e => setForm(p => ({ ...p, is_default: e.target.checked }))} style={{ width: 16, height: 16 }} />
                <label htmlFor="is_default" style={{ fontWeight: 600, color: '#344563', fontSize: 13, cursor: 'pointer' }}>Set as Default Preset</label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setShowForm(false)} style={{ background: '#F4F5F7', color: '#344563', border: 'none', borderRadius: 5, padding: '9px 20px', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ background: PRIMARY, color: '#fff', border: 'none', borderRadius: 5, padding: '9px 20px', cursor: 'pointer', fontWeight: 600 }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
